<template>
  <rs-side-panel ref="sidePanel">
    <template slot="header">Müşteriye Ürün Ekle</template>
    <template slot="body">
      <p>
        <strong>Dikkat:</strong> Seçilen müşterilerin var olan ürünü varsa
        sonlandırılacaktır.
      </p>
      <v-form
        @submit.prevent="handleFormSubmit"
        ref="form"
        :filled="isLoading"
        :readonly="isLoading"
      >
        <v-row cols="12" dense>
          <v-col sm="6" v-if="!id">
            <rs-select-customer
              v-model="formData.customer_ids"
              :rules="[rules.required]"
              @change:item="handleCustomerSelected"
              multiple
            >
            </rs-select-customer>
          </v-col>

          <v-col sm="6" v-else>
            <rs-select-customer
              v-model="formData.customer_id"
              :rules="[rules.required]"
              disabled
              :clearable="false"
            >
            </rs-select-customer>
          </v-col>

          <v-col sm="6">
            <rs-select-product
              label="Ürün Seçin"
              v-model="formData.product_id"
              :rules="[rules.required]"
              @change:item="handleProductSelected"
              :disabled="id"
              :clearable="!id"
            >
            </rs-select-product>
          </v-col>

          <v-col cols="12" sm="6">
            <rs-money-field
              :label="
                formData.is_yearly ? 'Yıllık Tutar / BB' : 'Aylık Tutar / BB'
              "
              v-model="formData.amount"
              :rules="[
                rules.required,
                rules.min(formData.amount, 0.0),
                rules.max(formData.amount, 999999.99),
              ]"
              :disabled="isLoading"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-switch
              :label="formData.is_yearly ? 'Yıllık' : 'Aylık'"
              v-model="formData.is_yearly"
              :rules="[rules.required]"
              @change="handleIsYearlyChanged"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <rs-datepicker
              label="Başlangıç Tarihi"
              v-model="formData.starts_on"
              :rules="[rules.required]"
              :disabled="formData.is_active"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <rs-datepicker
              label="Bitiş Tarihi"
              v-model="formData.ends_on"
              :disabled="!formData.starts_on"
              :rules="
                !formData.starts_on || !formData.ends_on
                  ? []
                  : [rules.min(formData.ends_on, formData.starts_on)]
              "
              :min="formData.starts_on"
              hint="Boş bırakılırsa ürün açık uçlu kalır. Doldurulursa bu tarihten sonra sonlanmış sayılır"
              persistent-hint
            />
          </v-col>

          <v-col cols="12" v-if="transitionPreview.message">
            <v-alert dense outlined type="error" class="mb-0">
              {{ transitionPreview.message }}
            </v-alert>
          </v-col>

          <v-col cols="12" v-if="allWarnings.length > 0">
            <v-alert dense outlined type="warning" class="mb-0">
              <div v-for="message in allWarnings" :key="message">
                {{ message }}
              </div>
            </v-alert>
          </v-col>
        </v-row>

        <rs-form-buttons
          @cancel="handleCancelClick"
          :is-loading="isLoading"
          hide-submit
          @submit="handleFormSubmit"
        />
      </v-form>
    </template>
  </rs-side-panel>
</template>

<script>
import { hasForm, hasPermissions } from "@/view/mixins";

export default {
  mixins: [hasForm, hasPermissions],
  data() {
    return {
      id: null,
      selectedProduct: null,
      formData: {
        customer_id: null,
        customer_ids: [],
        product_id: null,
        starts_on: new Date().toJSON().split("T")[0],
        ends_on: null,
        amount: null,
        is_yearly: false,
      },
      transitionPreview: {
        canSave: true,
        message: null,
        warnings: [],
      },
      lastTransitionPreviewKey: null,
      relatedCustomerProducts: [],
    };
  },
  computed: {
    resolvedCustomerId() {
      if (this.formData.customer_id) {
        return Number(this.formData.customer_id);
      }

      if (Array.isArray(this.formData.customer_ids)) {
        if (this.formData.customer_ids.length === 1) {
          return Number(this.formData.customer_ids[0]);
        }
      }

      return null;
    },
    comparableCustomerProducts() {
      return this.relatedCustomerProducts
        .filter((item) => Number(item.id) !== Number(this.id))
        .filter(
          (item) => Boolean(item.is_yearly) === Boolean(this.formData.is_yearly)
        )
        .sort((a, b) => (a.starts_on || "").localeCompare(b.starts_on || ""));
    },
    allWarnings() {
      return [
        ...new Set([
          ...(this.transitionPreview.warnings || []),
          ...this.gapWarnings,
        ]),
      ];
    },
    gapWarnings() {
      if (!this.resolvedCustomerId || !this.formData.starts_on) {
        return [];
      }

      const warnings = [];
      const currentStart = this.parseDate(this.formData.starts_on);
      const currentEnd = this.formData.ends_on
        ? this.parseDate(this.formData.ends_on)
        : null;

      if (currentEnd && currentEnd < currentStart) {
        return [];
      }

      const previousProduct = [...this.comparableCustomerProducts]
        .filter(
          (item) => item.starts_on && item.starts_on < this.formData.starts_on
        )
        .sort((a, b) =>
          (b.starts_on || "").localeCompare(a.starts_on || "")
        )[0];

      if (previousProduct && previousProduct.ends_on) {
        const previousEnd = this.parseDate(previousProduct.ends_on);
        const gapStart = this.addDays(previousEnd, 1);
        const gapEnd = this.addDays(currentStart, -1);

        if (gapStart <= gapEnd) {
          warnings.push(
            `Bu ürün ile önceki ürün arasında ${this.diffInDays(
              gapStart,
              gapEnd
            )} günlük boşluk var (${this.formatDate(
              gapStart
            )} - ${this.formatDate(gapEnd)}). Bu günler faturalanmaz.`
          );
        }
      }

      if (currentEnd) {
        const nextProduct = this.comparableCustomerProducts
          .filter(
            (item) => item.starts_on && item.starts_on > this.formData.starts_on
          )
          .sort((a, b) =>
            (a.starts_on || "").localeCompare(b.starts_on || "")
          )[0];

        if (nextProduct && nextProduct.starts_on) {
          const nextStart = this.parseDate(nextProduct.starts_on);
          const gapStart = this.addDays(currentEnd, 1);
          const gapEnd = this.addDays(nextStart, -1);

          if (gapStart <= gapEnd) {
            warnings.push(
              `Bu ürün ile sonraki ürün arasında ${this.diffInDays(
                gapStart,
                gapEnd
              )} günlük boşluk var (${this.formatDate(
                gapStart
              )} - ${this.formatDate(gapEnd)}). Bu günler faturalanmaz.`
            );
          }
        }
      }

      return warnings;
    },
  },
  watch: {
    resolvedCustomerId: {
      handler() {
        this.loadRelatedCustomerProducts();
        this.loadTransitionPreview();
      },
      immediate: false,
    },
    "formData.customer_ids": {
      handler() {
        this.loadRelatedCustomerProducts();
        this.loadTransitionPreview();
      },
      deep: true,
    },
    "formData.product_id"() {
      this.loadTransitionPreview();
    },
    "formData.starts_on"() {
      this.loadTransitionPreview();
    },
    "formData.ends_on"() {
      this.loadTransitionPreview();
    },
    "formData.is_yearly"() {
      this.loadTransitionPreview();
    },
  },
  methods: {
    resetTransitionPreview() {
      this.transitionPreview = {
        canSave: true,
        message: null,
        warnings: [],
      };
      this.lastTransitionPreviewKey = null;
    },
    isCompleteIsoDate(value) {
      return /^\d{4}-\d{2}-\d{2}$/.test(value || "");
    },
    getTransitionPreviewKey() {
      return JSON.stringify({
        customerId: this.resolvedCustomerId,
        customerProductId: this.id,
        productId: this.formData.product_id,
        startsOn: this.formData.starts_on,
        endsOn: this.formData.ends_on || null,
        isYearly: this.formData.is_yearly ? 1 : 0,
      });
    },
    resetFormData() {
      this.formData = {
        customer_id: null,
        customer_ids: [],
        product_id: null,
        starts_on: new Date().toJSON().split("T")[0],
        ends_on: null,
        amount: null,
        is_yearly: false,
      };
      this.resetTransitionPreview();
      this.relatedCustomerProducts = [];
    },
    show(customerId, productId, id) {
      this.id = null;
      this.resetFormData();

      if (productId) {
        this.formData.product_id = productId;
      }

      if (customerId) {
        this.formData.customer_ids = [customerId];
      }

      if (id) {
        this.id = id;
        this.load();
      } else {
        this.loadRelatedCustomerProducts();
      }

      this.$nextTick(() => {
        this.$refs.sidePanel.show();
      });
    },
    load() {
      this.isLoading = true;

      return this.$api
        .query(`api-super-admin/customer-products/${this.id}`)
        .then((response) => {
          this.loadData(response);
          this.loadRelatedCustomerProducts();
          this.loadTransitionPreview();
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    canSubmitForm() {
      const hasCustomerSelection = this.id
        ? Boolean(this.formData.customer_id)
        : Array.isArray(this.formData.customer_ids) &&
          this.formData.customer_ids.length > 0;
      const amount = Number(this.formData.amount);

      if (!hasCustomerSelection) {
        return false;
      }

      if (!this.formData.product_id) {
        return false;
      }

      if (!this.isCompleteIsoDate(this.formData.starts_on)) {
        return false;
      }

      if (
        this.formData.ends_on &&
        !this.isCompleteIsoDate(this.formData.ends_on)
      ) {
        return false;
      }

      if (
        this.formData.ends_on &&
        this.formData.ends_on < this.formData.starts_on
      ) {
        return false;
      }

      if (
        this.formData.amount === null ||
        this.formData.amount === undefined ||
        this.formData.amount === "" ||
        Number.isNaN(amount)
      ) {
        return false;
      }

      if (amount < 0 || amount > 999999.99) {
        return false;
      }

      return true;
    },
    async handleFormSubmit() {
      this.$refs.form.validate();

      if (this.isLoading || !this.canSubmitForm()) {
        this.$toast.error("Lütfen tüm gerekli alanları doldurunuz");
        return false;
      }

      const preview = await this.loadTransitionPreview();

      if (preview.canSave === false) {
        this.$toast.error(
          preview.message || "Bu müşteri ürünü bu tarihlerle kaydedilemez."
        );
        return false;
      }

      this.isLoading = true;
      const formData = Object.assign({}, this.formData);

      if (this.id) {
        this.$api
          .put(`api-super-admin/customer-products/${this.id}`, formData)
          .then(() => {
            this.$toast.success("Kaydedildi");
            this.$emit("saved");
            this.hide();
          })
          .catch((error) => {
            this.handleError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        if (formData.customer_id) {
          delete formData.customer_ids;
        } else {
          delete formData.customer_id;
        }

        this.$api
          .post(`api-super-admin/customer-products`, formData)
          .then(() => {
            this.$toast.success("Kaydedildi");
            this.$emit("saved");
            this.hide();
          })
          .catch((error) => {
            this.handleError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    handleProductSelected(product) {
      this.selectedProduct = product;

      if (this.formData.is_yearly) {
        this.formData.amount = this.selectedProduct.yearly_price_per_house;
      } else {
        this.formData.amount = this.selectedProduct.monthly_price_per_house;
      }
    },
    handleIsYearlyChanged(isYearly) {
      if (this.selectedProduct === null) {
        return;
      }

      if (isYearly) {
        this.formData.amount = this.selectedProduct.yearly_price_per_house;
      } else {
        this.formData.amount = this.selectedProduct.monthly_price_per_house;
      }
    },
    handleCustomerSelected() {
      this.loadRelatedCustomerProducts();
    },
    handleCancelClick() {
      this.hide();
    },
    hide() {
      this.$refs.sidePanel.hide();
    },
    loadRelatedCustomerProducts() {
      if (!this.resolvedCustomerId) {
        this.relatedCustomerProducts = [];
        return Promise.resolve();
      }

      return this.$api
        .query("api-super-admin/customer-products", {
          params: {
            customer_id: this.resolvedCustomerId,
            per_page: 500,
            page: 1,
            order_by: "starts_on",
            order: "asc",
          },
        })
        .then((response) => {
          this.relatedCustomerProducts = response.data.data || [];
        })
        .catch((error) => {
          this.relatedCustomerProducts = [];
          this.handleError(error);
        });
    },
    loadTransitionPreview() {
      if (
        !this.resolvedCustomerId ||
        !this.formData.product_id ||
        !this.isCompleteIsoDate(this.formData.starts_on) ||
        (this.formData.ends_on &&
          !this.isCompleteIsoDate(this.formData.ends_on))
      ) {
        this.resetTransitionPreview();
        return Promise.resolve(this.transitionPreview);
      }

      const previewKey = this.getTransitionPreviewKey();

      if (previewKey === this.lastTransitionPreviewKey) {
        return Promise.resolve(this.transitionPreview);
      }

      this.lastTransitionPreviewKey = previewKey;

      return this.$api
        .query("api-super-admin/customer-products/preview-transition", {
          params: {
            customer_id: this.resolvedCustomerId,
            product_id: this.formData.product_id,
            starts_on: this.formData.starts_on,
            ends_on: this.formData.ends_on,
            is_yearly: this.formData.is_yearly ? 1 : 0,
            customer_product_id: this.id,
          },
        })
        .then((response) => {
          const data = response?.data?.data || {};

          this.transitionPreview = {
            canSave: data.can_save !== false,
            message: data.message || null,
            warnings: Array.isArray(data.warnings) ? data.warnings : [],
          };

          return this.transitionPreview;
        })
        .catch((error) => {
          this.resetTransitionPreview();

          if (error?.response?.status !== 422) {
            this.handleError(error);
          }

          return this.transitionPreview;
        });
    },
    parseDate(value) {
      return new Date(`${value}T00:00:00`);
    },
    addDays(date, days) {
      const nextDate = new Date(date.getTime());
      nextDate.setDate(nextDate.getDate() + days);

      return nextDate;
    },
    diffInDays(startDate, endDate) {
      const dayMs = 24 * 60 * 60 * 1000;
      return Math.floor((endDate - startDate) / dayMs) + 1;
    },
    formatDate(date) {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `${day}.${month}.${year}`;
    },
  },
};
</script>
